import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  DatePicker,
  Input,
  Modal,
  Select,
} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { updateTherapyOccurrenceStatus } from '../../api/therapyOccurrencesApi';
import { getTherapyOccurrenceRequestStatusLabel } from '../../../therapyOccurrenceRequests/utils/therapyOccurrenceRequestPresentation';
import fixedBottomDatePickerProps from '../../../../shared/utils/datePickerPlacement';
import { useAuth } from '../../../../context/useAuth';
import { isSuperAdmin } from '../../../../shared/utils/roles';

const statusOptions = [
  { value: 'unavailable', label: 'Nedostupna' },
  { value: 'available', label: 'Dostupna' },
  { value: 'taken', label: 'Preuzeta' },
  { value: 'saved', label: 'Sacuvana' },
  { value: 'missed', label: 'Propustena' },
  // Primarno automatski status (istekao rok preuzimanja), ali ga backend
  // dozvoljava i rucno; ostaje u listi da se instanca ne prikaze prazno.
  { value: 'unassigned', label: 'Nedodeljeno' },
];

const UpdateOccurrenceStatusModal = ({
  isOpen,
  occurrence,
  onClose,
  onUpdated,
  onRefetchTherapies,
}) => {
  const { user } = useAuth();
  // Obicni admini ne mogu da menjaju status nedostupne instance; mogu samo
  // da je oznace kao rezervisanu, upisu napomenu i datum izmene.
  const isStatusLocked =
    !isSuperAdmin(user) && occurrence?.status === 'unavailable';
  const [status, setStatus] = useState('unavailable');
  const [isReserved, setIsReserved] = useState(false);
  const [note, setNote] = useState('');
  const [changeDate, setChangeDate] = useState(() => dayjs());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setStatus(occurrence?.status || 'unavailable');
    setIsReserved(
      occurrence?.secondaryStatus === 'reserved' ||
        occurrence?.status === 'reserved'
    );
    setNote(occurrence?.note || '');
    // Uvek predlozi danasnji datum; polje ne pamti prethodni izbor.
    setChangeDate(dayjs());
    setError('');
    setLoading(false);
  }, [
    isOpen,
    occurrence?._id,
    occurrence?.status,
    occurrence?.secondaryStatus,
    occurrence?.note,
  ]);

  const handleSave = async () => {
    if (!occurrence?._id) return;
    setLoading(true);
    setError('');
    const {
      ok,
      message,
      status: httpStatus,
      data,
    } = await updateTherapyOccurrenceStatus(occurrence._id, {
      status: isStatusLocked ? occurrence.status : status,
      secondaryStatus: isReserved ? 'reserved' : null,
      note: note.trim() || undefined,
      changeDate: changeDate ? changeDate.format('YYYY-MM-DD') : undefined,
    });
    if (!ok) {
      setError(httpStatus ? `${message} )` : message);
      setLoading(false);
      return;
    }

    const updatedOccurrence =
      data?.occurrence || data?.data?.occurrence || data?.data || null;

    await onUpdated?.(updatedOccurrence);
    onRefetchTherapies?.();
    onClose();
    setLoading(false);
  };

  return (
    <Modal
      className="therapy_status_modal"
      title="Promeni status instance"
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button
          key="save"
          type="primary"
          loading={loading}
          onClick={handleSave}
        >
          Sacuvaj
        </Button>,
      ]}
    >
      {occurrence?.request && (
        <div style={{ marginBottom: 12 }}>
          <strong>Zahtev:</strong> {occurrence.request.pickupMethod || '-'} /{' '}
          {getTherapyOccurrenceRequestStatusLabel(occurrence.request.status)}
        </div>
      )}
      {isStatusLocked && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          title="Instanca je nedostupna. Status moze da promeni samo super admin; mozete oznaciti rezervaciju, upisati napomenu i datum izmene."
        />
      )}
      <Select
        style={{ width: '100%' }}
        value={status}
        onChange={setStatus}
        options={statusOptions}
        disabled={isStatusLocked}
      />
      <Checkbox
        style={{ marginTop: 12 }}
        checked={isReserved}
        onChange={(event) => setIsReserved(event.target.checked)}
      >
        Rezervisana
      </Checkbox>
      <Input.TextArea
        style={{ marginTop: 12 }}
        rows={3}
        placeholder="Napomena (opciono)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div style={{ marginTop: 12 }}>
        <label
          htmlFor="occurrence-change-date"
          style={{ display: 'block', marginBottom: 4 }}
        >
          Datum izmene
        </label>
        <DatePicker
          {...fixedBottomDatePickerProps}
          id="occurrence-change-date"
          style={{ width: '100%' }}
          format="DD.MM.YYYY"
          allowClear={false}
          value={changeDate}
          onChange={(date) => setChangeDate(date)}
        />
      </div>
      {!!error && (
        <Alert title={error} type="error" showIcon style={{ marginTop: 16 }} />
      )}
    </Modal>
  );
};

UpdateOccurrenceStatusModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  occurrence: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onUpdated: PropTypes.func,
  onRefetchTherapies: PropTypes.func,
};

export default UpdateOccurrenceStatusModal;
